import { IntruderVideoClip } from '../types';
import { Loader2, AlertCircle, Video, Expand, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useContextualTimeZone } from '@/components/TimezoneProvider';

interface IntruderVideoClipPlayerProps {
  videoClip: IntruderVideoClip;
  onExpand?: () => void;
}

/**
 * Formats a date string for display in the given timezone
 */
function formatDateTime(dateString: string | undefined, timeZone?: string): string {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone,
    });
  } catch {
    return '';
  }
}

/**
 * Formats a date for the expiry display in the given timezone
 */
function formatDate(dateString: string | undefined, timeZone?: string): string {
  if (!dateString) return '';
  try {
    return new Date(dateString).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone,
    });
  } catch {
    return '';
  }
}

export function IntruderVideoClipPlayer({ videoClip, onExpand }: IntruderVideoClipPlayerProps) {
  const { timeZone } = useContextualTimeZone();

  // Pending or Processing state
  if (videoClip.status === 'Pending' || videoClip.status === 'Processing') {
    return (
      <div className="rounded-lg border border-[#2E3835] bg-[#1b1b1b] p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#222]">
            <Loader2 className="h-5 w-5 animate-spin text-lime" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Generating video clip...</p>
            <p className="text-xs text-gray-400">
              This may take a few minutes. The video will appear here when ready.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  if (videoClip.status === 'Failed') {
    return (
      <div className="rounded-lg border border-red-900/50 bg-[#1b1b1b] p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-900/20">
            <AlertCircle className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-red-400">Failed to capture video</p>
            <p className="text-xs text-gray-400">
              {videoClip.errorMessage || 'An error occurred while generating the video clip.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Ready state - show the video player
  if (videoClip.status === 'Ready' && videoClip.cloudflareEmbedUrl) {
    return (
      <div className="overflow-hidden rounded-lg border border-[#2E3835] bg-[#1b1b1b]">
        {/* Header */}
        <div className="border-b border-[#2E3835] p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Video className="h-4 w-4 text-lime" />
              <h4 className="text-sm font-medium text-lime">Incident Video Clip</h4>
            </div>
            <div className="flex items-center gap-1">
              {videoClip.cloudflareDownloadUrl && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-lime hover:text-lime"
                  asChild
                >
                  <a href={videoClip.cloudflareDownloadUrl} download>
                    <Download className="h-3.5 w-3.5" />
                  </a>
                </Button>
              )}
              {onExpand && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-lime hover:text-lime"
                  onClick={onExpand}
                >
                  <Expand className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
          {videoClip.startTime && videoClip.endTime && (
            <p className="mt-1 text-xs text-gray-400">
              {formatDateTime(videoClip.startTime, timeZone)} - {formatDateTime(videoClip.endTime, timeZone)}
            </p>
          )}
          {videoClip.expiresAt && (
            <p className="mt-1 text-xs text-gray-500">
              Video expires: {formatDate(videoClip.expiresAt, timeZone)}
            </p>
          )}
        </div>

        {/* Description */}
        <div className="border-b border-[#2E3835] bg-[#222] px-3 py-2">
          <p className="text-xs text-gray-400">
            This is the representation of the incident that the agent picked.
          </p>
        </div>

        {/* Video iframe */}
        <div className="aspect-video">
          <iframe
            src={videoClip.cloudflareEmbedUrl}
            className="h-full w-full border-none"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  // Fallback - no video available
  return null;
}
